import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginModal from './auth/LoginModal';
import RegisterModal from './auth/RegisterModal';
import ResetPasswordModal from './auth/ResetPasswordModal';
import UpdateAvatarModal from './user/UpdateAvatarModal';
import UpdateNameModal from './user/UpdateNameModal';
import Avatar from './Avatar';
import Message from './Message';
import ImagePreview from './ImagePreview';

const SunIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="5" fill="currentColor"/>
    <path d="M12 1v2M12 21v2M1 12h2M21 12h2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const MoonIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" fill="currentColor" fillOpacity="0.5" strokeWidth="0"/>
  </svg>
);

const WeChatIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 4.882-1.978 7.272-1.978-.276-3.399-3.73-6.198-8.015-6.198zm-2.43 5.695a.911.911 0 0 1 .917-.917.911.911 0 0 1 .917.917.911.911 0 0 1-.917.917.911.911 0 0 1-.917-.917zm5.22 0a.911.911 0 0 1 .917-.917.911.911 0 0 1 .917.917.911.911 0 0 1-.917.917.911.911 0 0 1-.917-.917z" fill="currentColor"/>
    <path d="M16.735 11.178c-4.357 0-7.897 2.896-7.897 6.461 0 1.847.992 3.508 2.509 4.642a.493.493 0 0 1 .178.555l-.326 1.236c-.016.058-.04.117-.04.178 0 .136.108.246.243.246a.272.272 0 0 0 .139-.045l1.591-.93a.722.722 0 0 1 .599-.082 8.5 8.5 0 0 0 2.37.336c4.357 0 7.897-2.896 7.897-6.461s-3.54-6.136-7.863-6.136zm-3.825 4.633a.761.761 0 0 1 .765-.765.761.761 0 0 1 .765.765.761.761 0 0 1-.765.765.761.761 0 0 1-.765-.765zm4.357 0a.761.761 0 0 1 .765-.765.761.761 0 0 1 .765.765.761.761 0 0 1-.765.765.761.761 0 0 1-.765-.765z" fill="currentColor"/>
  </svg>
);

const LayoutWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.background};
  transition: all 0.3s ease;
`;

const MessageContainer = styled.div.attrs({ id: 'layout-message' })`
  position: fixed;
  top: 64px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 999;
  pointer-events: none;
  display: flex;
  justify-content: center;

  @media (max-width: 480px) {
    top: 56px;
  }
`;

const Header = styled.header`
  background: ${({ theme }) => theme.isDarkMode ? '#333333' : 'var(--bg-body-secondary, #E8E8E8)'};
  padding: 0;
  box-shadow: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 990;
  height: 56px;
  display: flex;
  align-items: center;

  @media (max-width: 480px) {
    height: 56px;
    padding: 0;
  }
`;

const HeaderContent = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  position: relative;

  @media (max-width: 1440px) {
    max-width: 100%;
    padding: 0 24px;
  }

  @media (max-width: 1024px) {
    padding: 0 24px;
  }

  @media (max-width: 768px) {
    padding: 0 24px;
  }

  @media (max-width: 480px) {
    padding: 0 24px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 0;

  @media (max-width: 768px) {
    gap: 24px;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const Logo = styled.div`
  font-size: 0.875rem;
  color: ${({ $active, theme }) => $active ? '#FF4D4F' : theme.text};
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: #FF4D4F;
  }

  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const CenterLogo = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  
  img {
    height: 16px;
    width: 300px;
    max-width: 100%;
    filter: ${({ theme }) => theme.text === '#ffffff' ? 'brightness(0) invert(1)' : 'none'};
    transition: filter 0.3s ease;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ThemeToggle = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
  
  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.isDarkMode ? '#AAAAAA' : '#333333'};
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const WeChatButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
  
  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.isDarkMode ? '#AAAAAA' : '#333333'};
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  margin-left: 0;

  @media (max-width: 768px) {
    gap: 24px;
    margin-left: 0;
  }
`;

const NavLink = styled.div`
  color: ${({ $active, theme }) => $active ? '#FF4D4F' : theme.text};
  font-size: 0.875rem;
  cursor: pointer;
  transition: color 0.3s ease;
  white-space: nowrap;
  height: 32px;
  display: flex;
  align-items: center;

  &:hover {
    color: #FF4D4F;
  }

  @media (max-width: 768px) {
    font-size: 0.8125rem;
    height: 32px;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    height: 32px;
  }
`;

const UserName = styled.span`
  color: ${({ theme }) => theme.text};
  font-size: 14px;
  margin-right: 8px;
  white-space: nowrap;
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const Main = styled.main`
  flex: 1;
  margin-top: 56px;
  padding: 1.5rem 0;

  @media (max-width: 768px) {
    margin-top: 56px;
    padding: 1rem 0;
  }
`;

const MainContent = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0;
  width: 100%;

  @media (max-width: 1440px) {
    max-width: 100%;
    padding: 0;
  }

  @media (max-width: 1024px) {
    padding: 0;
  }

  @media (max-width: 768px) {
    padding: 0;
  }

  @media (max-width: 480px) {
    padding: 0;
  }
`;

const Footer = styled.footer`
  background: transparent;
  padding: 1rem 0;
  text-align: center;
  color: ${({ theme }) => theme.secondaryText};
  width: 100%;
  margin-top: auto;
  position: relative;
  z-index: 1;
`;

const FooterContent = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 0.3rem;
  width: 100%;
  text-align: center;

  @media (max-width: 1440px) {
    max-width: 100%;
  }

  @media (max-width: 1024px) {
    padding: 0 0.25rem;
  }

  @media (max-width: 768px) {
    padding: 0 0.2rem;
    font-size: 0.875rem;
  }

  @media (max-width: 480px) {
    padding: 0 0.15rem;
  }
`;

const LoginButton = styled.button`
  width: 64px;
  height: 40px;
  padding-top: 7px;
  padding-right: 16px;
  padding-bottom: 7px;
  padding-left: 16px;
  gap: 8px;
  border-radius: 200px;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#FFFFFF'};
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : '#333'};
  border: ${({ theme }) => theme.isDarkMode ? 'none' : '1px solid rgba(0, 0, 0, 0.1)'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.8)'};
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const Layout = ({ children }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { 
    showLoginModal, 
    hideModals, 
    isLoginVisible, 
    isRegisterVisible, 
    isResetPasswordVisible,
    showRegisterModal,
    showResetPasswordModal
  } = useAuth();
  const { user, loading, logout } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [isUpdateAvatarVisible, setIsUpdateAvatarVisible] = useState(false);
  const [isUpdateNameVisible, setIsUpdateNameVisible] = useState(false);
  const [avatarKey, setAvatarKey] = useState(Date.now());
  const [isWeChatImageVisible, setIsWeChatImageVisible] = useState(false);

  useEffect(() => {
    if (user?.userPic) {
      setAvatarKey(Date.now());
    }
  }, [user?.userPic]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogin = () => {
    showLoginModal();
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const showUpdateAvatarModal = () => {
    setIsUpdateAvatarVisible(true);
    setShowDropdown(false);
  };

  const showUpdateNameModal = () => {
    setIsUpdateNameVisible(true);
    setShowDropdown(false);
  };

  const hideUserModals = () => {
    setIsUpdateAvatarVisible(false);
    setIsUpdateNameVisible(false);
    setAvatarKey(Date.now());
  };

  const handleLogout = () => {
    Message.success('已退出登录', 'layout-message');
    logout();
    navigate('/');
    setShowDropdown(false);
  };

  const handleWeChatClick = () => {
    setIsWeChatImageVisible(true);
  };

  const handleCloseWeChatImage = () => {
    setIsWeChatImageVisible(false);
  };

  console.log('Current user:', user);
  console.log('Current path:', location.pathname);

  return (
    <LayoutWrapper>
      <MessageContainer />
      <Header>
        <HeaderContent>
          <LeftSection>
            <NavLinks>
              <NavLink 
                onClick={() => handleNavigation('/')} 
                $active={location.pathname === '/'}
              >
                作品展示
              </NavLink>
              {!loading && user && (
                <>
                  <NavLink 
                    onClick={() => handleNavigation('/history')} 
                    $active={location.pathname === '/history'}
                  >
                    历史对战
                  </NavLink>
                  {user.role === 'admin' && (
                    <NavLink 
                      onClick={() => handleNavigation('/battle-settings')} 
                      $active={location.pathname === '/battle-settings'}
                    >
                      发起对战
                    </NavLink>
                  )}
                </>
              )}
            </NavLinks>
          </LeftSection>
          <CenterLogo>
            <img src="/BATTLE UNIVERSE.svg" alt="BATTLE UNIVERSE" />
          </CenterLogo>
          <RightSection>
            {!loading && (
              user ? (
                <>
                  <WeChatButton onClick={handleWeChatClick}>
                    <WeChatIcon />
                  </WeChatButton>
                  <ThemeToggle onClick={toggleTheme}>
                    {isDarkMode ? (
                      <SunIcon />
                    ) : (
                      <MoonIcon />
                    )}
                  </ThemeToggle>
                  <AvatarDropdown ref={dropdownRef}>
                    <AvatarButton onClick={() => setShowDropdown(!showDropdown)}>
                      <Avatar 
                        key={user.userPic}
                        src={user.userPic ? 
                          (user.userPic.startsWith('http') ? 
                            user.userPic 
                            : `https://static.battleverse.cn/userpic/${user.userPic.replace('avatars/', '')}`
                          ) 
                          : ''} 
                        name={user.userName}
                        size={40}
                      />
                    </AvatarButton>
                    <DropdownMenu $show={showDropdown}>
                      <MenuItem onClick={showUpdateNameModal}>修改用户名</MenuItem>
                      <MenuItem onClick={showUpdateAvatarModal}>更换头像</MenuItem>
                      <MenuItem onClick={handleLogout}>退出登录</MenuItem>
                    </DropdownMenu>
                  </AvatarDropdown>
                </>
              ) : (
                <>
                  <WeChatButton onClick={handleWeChatClick}>
                    <WeChatIcon />
                  </WeChatButton>
                  <ThemeToggle onClick={toggleTheme}>
                    {isDarkMode ? (
                      <SunIcon />
                    ) : (
                      <MoonIcon />
                    )}
                  </ThemeToggle>
                  <LoginButton onClick={handleLogin}>登录</LoginButton>
                </>
              )
            )}
          </RightSection>
        </HeaderContent>
      </Header>

      <Main>
        <MainContent>{children}</MainContent>
      </Main>

      <Footer>
        <FooterContent>
          <IcpBeian>
            <a href="http://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
              Copyright©2025 赣ICP备2025056382号-1
            </a>
          </IcpBeian>
        </FooterContent>
      </Footer>

      {isLoginVisible && (
        <LoginModal onClose={hideModals} onShowRegister={showRegisterModal} onShowResetPassword={showResetPasswordModal} />
      )}
      {isRegisterVisible && (
        <RegisterModal onClose={hideModals} onShowLogin={showLoginModal} />
      )}
      {isResetPasswordVisible && (
        <ResetPasswordModal onClose={hideModals} onShowLogin={showLoginModal} />
      )}
      {isUpdateAvatarVisible && (
        <UpdateAvatarModal onClose={hideUserModals} />
      )}
      {isUpdateNameVisible && (
        <UpdateNameModal onClose={hideUserModals} />
      )}
      <ImagePreview
        visible={isWeChatImageVisible}
        onClose={handleCloseWeChatImage}
        images={['/bu网站二维码.png']}
        initialIndex={0}
        captions={['网站二维码']}
      />
    </LayoutWrapper>
  );
};

const UserInfo = styled.div`  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const IcpBeian = styled.div`
  float: left;
  width: 100%;
  height: 100px;
  margin-top: 8px;
  font-size: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  
  a {
    color: ${({ theme }) => theme.secondaryText};
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: #FF4D4F;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const AvatarDropdown = styled.div`  position: relative;
  display: inline-block;
`;

const AvatarButton = styled.div`
  cursor: pointer;
  transition: all 0.3s ease;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: ${({ theme }) => theme.cardBg};
  border-radius: 12px;
  padding: 4px 0;
  min-width: 120px;
  box-shadow: none;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transform: ${({ $show }) => ($show ? 'translateY(0)' : 'translateY(-8px)')};
  visibility: ${({ $show }) => ($show ? 'visible' : 'hidden')};
  transition: all 0.2s ease;
  z-index: 1000;
`;

const MenuItem = styled.div`
  padding: 10px 16px;
  cursor: pointer;
  color: ${({ theme }) => theme.text};
  transition: all 0.2s ease;
  font-size: 14px;
  border-radius: 16px;
  margin: 4px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
    color: ${({ theme }) => theme.text};
    transform: none;
    box-shadow: none;
  }

  &:active {
    transform: none;
    box-shadow: none;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.border};
  margin: 6px 8px;
  opacity: 0.6;
`;

export default Layout;